import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import DrawerTab from 'shared/components/ui/DrawerTab';
import Modal, { buttonClass, primaryButtonClass, inputClass, labelClass } from 'features/dashboards/components/Modal';
import { CardBody, Center } from 'features/dashboards/components/DashboardCard';
import { getFields, getPresets, previewCard } from 'features/dashboards/api/dashboardsApi';
import { errorText } from 'features/dashboards/format';
import ChartTypePicker from 'features/dashboards/components/ChartTypePicker';
import { RichTextEditor } from 'features/dashboards/components/RichText';

const TAB_LABELS = { overview: 'Overview', cost: 'Cost', latency: 'Latency', usage: 'Usage', evals: 'Evals' };
const FNS = [
  ['count', 'Count'], ['distinct', 'Distinct'], ['sum', 'Total'], ['avg', 'Average'], ['min', 'Min'], ['max', 'Max'],
  ['p50', 'p50'], ['p90', 'p90'], ['p95', 'p95'], ['p99', 'p99'], ['rate', 'Share where'],
];
const OPS = [['eq', 'is'], ['neq', 'is not'], ['contains', 'contains'], ['gt', '>'], ['lt', '<'], ['is_set', 'is set'], ['is_not_set', 'is not set']];

/** The app's dropdown, fed [value, label] pairs. */
function Pick({ value, options, onChange, ariaLabel, disabled, className = '' }) {
  return (
    <div className={className || 'min-w-0 flex-1'}>
      <BrandDropdown value={value} ariaLabel={ariaLabel} disabled={disabled}
        options={options.map(([v, l]) => ({ value: v, label: l }))} onChange={onChange} />
    </div>
  );
}

function needsField(fn) { return fn !== 'count'; }

/** The measure's field choices: numbers for math, labels for distinct, yes/no fields for "share where". */
function fieldsFor(fn, fields) {
  if (fn === 'distinct') return fields.filter((f) => f.type === 'string');
  if (fn === 'rate') return fields.filter((f) => f.type === 'boolean');
  return fields.filter((f) => f.type === 'number');
}

function toSpec(b) {
  const measures = b.measures.map((m) => (m.fn === 'rate'
    ? { fn: 'rate', where: [{ field: m.field, op: 'is_true' }] }
    : { fn: m.fn, ...(needsField(m.fn) ? { field: m.field } : {}) }));
  const where = b.where.filter((w) => w.field && (['is_set', 'is_not_set'].includes(w.op) || w.value !== ''))
    .map((w) => ({ field: w.field, op: w.op, ...(['is_set', 'is_not_set'].includes(w.op) ? {} : { value: ['gt', 'lt'].includes(w.op) ? Number(w.value) : w.value }) }));
  return {
    entity: b.entity,
    measures,
    ...(b.overTime ? { time: {} } : {}),
    ...(b.groupBy ? { dimension: { field: b.groupBy, top: Number(b.top) || 12 } } : {}),
    ...(b.splitBy ? { series: { field: b.splitBy, top: 6 } } : {}),
    ...(where.length ? { where } : {}),
  };
}

function Builder({ entities, value, onChange }) {
  const entity = entities[value.entity];
  const fields = entity ? entity.fields : [];
  const labels = fields.filter((f) => f.dimension);
  const set = (patch) => onChange({ ...value, ...patch });
  const setMeasure = (i, patch) => set({ measures: value.measures.map((m, j) => (j === i ? { ...m, ...patch } : m)) });

  return (
    <div className="space-y-3">
      <div>
        <span className={labelClass}>Of</span>
        <Pick ariaLabel="Of" value={value.entity} options={Object.entries(entities).map(([id, e]) => [id, e.label])}
          onChange={(v) => set({ entity: v, measures: [{ fn: 'count', field: '' }], groupBy: '', splitBy: '', where: [] })} />
      </div>

      <div>
        <span className={labelClass}>Show</span>
        <div className="space-y-1.5">
          {value.measures.map((m, i) => {
            const choices = fieldsFor(m.fn, fields);
            return (
              <div key={i} className="flex items-center gap-1.5">
                <Pick className="w-36 shrink-0" ariaLabel="Function" value={m.fn} options={FNS}
                  onChange={(fn) => { const c = fieldsFor(fn, fields); setMeasure(i, { fn, field: needsField(fn) ? (c.find((f) => f.key === m.field) ? m.field : (c[0]?.key || '')) : '' }); }} />
                {needsField(m.fn) ? (
                  <Pick ariaLabel="Field" value={m.field} options={choices.map((f) => [f.key, f.label])} onChange={(v) => setMeasure(i, { field: v })} />
                ) : <div className="flex-1" />}
                {value.measures.length > 1 ? (
                  <button type="button" aria-label="Remove" onClick={() => set({ measures: value.measures.filter((_, j) => j !== i) })}
                    className="rounded p-1.5 text-[color:var(--text-muted)] hover:bg-surface-hover"><Trash2 className="h-3.5 w-3.5" /></button>
                ) : null}
              </div>
            );
          })}
          {value.measures.length < 4 ? (
            <button type="button" onClick={() => set({ measures: [...value.measures, { fn: 'count', field: '' }] })}
              className="inline-flex items-center gap-1 text-[12px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">
              <Plus className="h-3.5 w-3.5" /> Measure
            </button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className={labelClass}>Group by</span>
          <Pick ariaLabel="Group by" value={value.groupBy} options={[['', 'None'], ...labels.map((f) => [f.key, f.label])]} onChange={(v) => set({ groupBy: v })} />
        </div>
        <div>
          <span className={labelClass}>Split by</span>
          <Pick ariaLabel="Split by" value={value.splitBy} disabled={!value.overTime && !value.groupBy}
            options={[['', 'None'], ...labels.filter((f) => f.key !== value.groupBy).map((f) => [f.key, f.label])]} onChange={(v) => set({ splitBy: v })} />
        </div>
      </div>

      <label className="flex items-center gap-2 text-[12px] text-[color:var(--text-primary)]">
        <input type="checkbox" className="h-3.5 w-3.5 accent-[color:var(--trodo-primary)]" checked={value.overTime} onChange={(e) => set({ overTime: e.target.checked, ...(e.target.checked && value.groupBy && value.splitBy ? { splitBy: '' } : {}) })} />
        Over time
      </label>

      <div>
        <span className={labelClass}>Where</span>
        <div className="space-y-1.5">
          {value.where.map((w, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Pick ariaLabel="Field" value={w.field} options={fields.filter((f) => f.type !== 'time').map((f) => [f.key, f.label])}
                onChange={(v) => set({ where: value.where.map((x, j) => (j === i ? { ...x, field: v } : x)) })} />
              <Pick className="w-28 shrink-0" ariaLabel="Operator" value={w.op} options={OPS}
                onChange={(v) => set({ where: value.where.map((x, j) => (j === i ? { ...x, op: v } : x)) })} />
              {['is_set', 'is_not_set'].includes(w.op) ? <div className="flex-1" /> : (
                <input className={inputClass} value={w.value} placeholder="value" aria-label="Value"
                  onChange={(e) => set({ where: value.where.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) })} />
              )}
              <button type="button" aria-label="Remove" onClick={() => set({ where: value.where.filter((_, j) => j !== i) })}
                className="rounded p-1.5 text-[color:var(--text-muted)] hover:bg-surface-hover"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <button type="button" onClick={() => set({ where: [...value.where, { field: labels[0]?.key || '', op: 'eq', value: '' }] })}
            className="inline-flex items-center gap-1 text-[12px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">
            <Plus className="h-3.5 w-3.5" /> Condition
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Add a card: a ready-made chart, or one built from the field dictionary with
 * a live preview that runs through the same path the saved card will.
 */
export default function AddCardDialog({ team, range, onAdd, onClose }) {
  const [mode, setMode] = useState('presets');
  const [presets, setPresets] = useState(null);
  const [entities, setEntities] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [chart, setChart] = useState('');
  const [doc, setDoc] = useState(null);
  const [framed, setFramed] = useState(false);
  const [b, setB] = useState({ entity: 'run', measures: [{ fn: 'count', field: '' }], groupBy: '', splitBy: '', overTime: true, top: 12, where: [] });
  const [preview, setPreview] = useState({ loading: false, result: null, error: null });

  useEffect(() => {
    getPresets().then(setPresets).catch((e) => setError(errorText(e)));
    getFields().then(setEntities).catch((e) => setError(errorText(e)));
  }, []);

  const spec = useMemo(() => toSpec(b), [b]);
  const card = useMemo(() => ({ kind: 'query', spec, viz: { title: title || null, ...(chart ? { chart } : {}) } }), [spec, chart, title]);

  useEffect(() => {
    if (mode !== 'custom' || !entities) return undefined;
    let alive = true;
    setPreview((p) => ({ ...p, loading: true, error: null }));
    const t = setTimeout(() => {
      previewCard(team, card, range)
        .then((result) => { if (alive) setPreview({ loading: false, result, error: null }); })
        .catch((e) => { if (alive) setPreview({ loading: false, result: null, error: errorText(e) }); });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [mode, entities, team, card, range]);

  const add = async (input) => {
    setSaving(true); setError(null);
    try { await onAdd(input); onClose(); } catch (e) { setError(errorText(e)); } finally { setSaving(false); }
  };

  const groups = useMemo(() => {
    const out = {};
    for (const p of presets || []) (out[p.tab] = out[p.tab] || []).push(p);
    return out;
  }, [presets]);

  const tabButton = (id, label) => (
    <DrawerTab id={id} label={label} active={mode === id} onClick={setMode} />
  );

  return (
    <Modal title="Add card" onClose={onClose} width={mode === 'presets' ? 'max-w-lg' : 'max-w-4xl'}
      footer={mode === 'custom' ? (
        <>
          <button type="button" className={buttonClass} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryButtonClass} disabled={saving || !!preview.error} onClick={() => add(card)}>Add card</button>
        </>
      ) : mode === 'text' ? (
        <>
          <label className="mr-auto flex items-center gap-2 text-[12px] text-[color:var(--text-primary)]">
            <input type="checkbox" className="h-3.5 w-3.5 accent-[color:var(--trodo-primary)]" checked={framed} onChange={(e) => setFramed(e.target.checked)} />
            Show as a card
          </label>
          <button type="button" className={buttonClass} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryButtonClass} disabled={saving || !doc}
            onClick={() => add({ kind: 'text', spec: { doc }, viz: framed ? {} : { frame: 'plain' }, w: 12, h: 4 })}>Add text</button>
        </>
      ) : null}>
      <div className="flex gap-1 border-b border-[color:var(--border-subtle)] px-4">
        {tabButton('presets', 'Presets')}
        {tabButton('custom', 'Custom')}
        {tabButton('text', 'Text')}
      </div>
      {error ? <div className="mx-4 mt-3 rounded-lg border border-[color:var(--feedback-error)] bg-[color:var(--feedback-error-bg)] px-3 py-2 text-[12px] text-[color:var(--feedback-error)]">{error}</div> : null}

      {mode === 'presets' ? (
        <div className="px-4 py-3">
          {!presets ? <div className="py-6 text-center text-[12px] text-[color:var(--text-muted)]">Loading…</div> : Object.entries(groups).map(([tab, list]) => (
            <div key={tab} className="mb-3">
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[color:var(--text-muted)]">{TAB_LABELS[tab] || tab}</div>
              <div className="grid grid-cols-2 gap-1">
                {list.map((p) => (
                  <button key={p.id} type="button" disabled={saving}
                    onClick={() => add({ kind: 'preset', spec: { presetId: p.id }, viz: { intent: p.viz.intent, title: p.title, series: p.viz.series || null, ...(p.viz.chart ? { chart: p.viz.chart } : {}) } })}
                    className="truncate rounded-md px-2 py-1.5 text-left text-[12px] text-[color:var(--text-primary)] hover:bg-surface-hover">
                    {p.title}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : mode === 'text' ? (
        <div className="px-4 py-3">
          <RichTextEditor initial={null} onChange={setDoc} placeholder="A heading, a note, what this tab is for…" />
        </div>
      ) : !entities ? (
        <div className="py-6 text-center text-[12px] text-[color:var(--text-muted)]">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 px-4 py-3 md:grid-cols-[300px_1fr]">
          <div className="space-y-3">
            <div>
              <label className={labelClass} htmlFor="dash-title">Title</label>
              <input id="dash-title" className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Untitled" />
            </div>
            <Builder entities={entities} value={b} onChange={setB} />
            <div>
              <span className={labelClass}>Chart</span>
              <ChartTypePicker value={chart} onChange={setChart} />
            </div>
          </div>
          <div className="flex min-h-[320px] flex-col rounded-lg border border-[color:var(--border-default)] p-3">
            <div className="mb-1 truncate text-[12px] font-medium text-[color:var(--text-primary)]">{title || 'Preview'}</div>
            <div className="min-h-0 flex-1">
              {preview.error ? <Center error>{preview.error}</Center>
                : preview.loading && !preview.result ? <Center muted>Loading…</Center>
                  : preview.result ? <CardBody result={preview.result} /> : null}
            </div>
            {preview.result?.note ? <div className="mt-1.5 text-[11px] text-[color:var(--text-muted)]">{preview.result.note}</div> : null}
          </div>
        </div>
      )}
    </Modal>
  );
}
